/**
 * Direct-to-brand vs Cabana split test, quote app side.
 *
 * After Slide 1 has a valid place and /api/allowed-zip passes, the funnel asks
 * /api/split-assign whether this visitor is in the test. The answer is cached
 * per stable id and normalized address for the browser session, and the
 * current answer drives three things: the direct-arm short form (Step), the
 * header phone number (Header) and the split_assignment_id stamped on
 * lead-submit, draft-save and service-options payloads for the Cabana arm.
 *
 * Everything fails open: a timeout, an error or a malformed answer means
 * "not in test" and the normal funnel continues.
 */
import { Statsig } from 'statsig-react';

export const SPLIT_ASSIGN_TIMEOUT_MS = 2500;
export const SPLIT_ASSIGN_GATE = 'split_test_assign_enabled';
export const SPLIT_EVENT = 'cabana:split-assignment';
const QA_KEY = 'split_qa';
const CURRENT_KEY = 'split_assignment_current';
const CACHE_PREFIX = 'split_assignment:';
const ARMS = ['direct', 'cabana'];

function storage() {
  try { return window.sessionStorage; } catch { return null; }
}

function readJson(key) {
  try {
    const raw = storage()?.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeJson(key, value) {
  try {
    if (value === null) storage()?.removeItem(key);
    else storage()?.setItem(key, JSON.stringify(value));
  } catch { /* storage full or blocked: the funnel still works */ }
}

/**
 * Reads ?splitqa=<token>&splitarm=direct|cabana once, keeps them for the
 * session, and strips both from the visible URL so they are not shared or
 * recorded as the landing page.
 */
export function captureSplitQaParams(win = window) {
  try {
    const url = new URL(win.location.href);
    const token = url.searchParams.get('splitqa');
    const arm = url.searchParams.get('splitarm');
    if (!url.searchParams.has('splitqa') && !url.searchParams.has('splitarm')) return;
    if (token && ARMS.includes(arm)) writeJson(QA_KEY, { token: token.slice(0, 256), arm });
    url.searchParams.delete('splitqa');
    url.searchParams.delete('splitarm');
    win.history.replaceState(win.history.state, '', url.pathname + url.search + url.hash);
  } catch { /* malformed URL: nothing to capture */ }
}

/**
 * The QA token for the direct-arm lead-submit payload, or undefined when no
 * QA override is active this session. The processor uses it to keep a QA
 * direct submit silent even if its assignment lookup fails.
 */
export function splitQaTokenForPayload() {
  return getSplitQa()?.token || undefined;
}

export function getSplitQa() {
  const qa = readJson(QA_KEY);
  return qa && typeof qa.token === 'string' && ARMS.includes(qa.arm) ? qa : null;
}

/**
 * The call is made only when the Statsig gate is on, or when a QA override is
 * present for this session. With the gate off, Slide 1 behaves exactly as it
 * did before this feature: no extra request, no extra wait.
 */
export function isSplitAssignEnabled() {
  if (getSplitQa()) return true;
  try { return Statsig.checkGateWithExposureLoggingDisabled(SPLIT_ASSIGN_GATE) === true; }
  catch { return false; }
}

function component(place, type, short = false) {
  const found = (place?.address_components || []).find(c => (c.types || []).includes(type));
  return found ? String(short ? found.short_name : found.long_name || '') : '';
}

/** Builds the contract's address object from a Google Places result. */
export function splitAddressFromPlace(place, { formatted = '', zip = '', lat = null, lng = null } = {}) {
  const street = [component(place, 'street_number'), component(place, 'route')].filter(Boolean).join(' ');
  return {
    line1: street || String(formatted).split(',')[0].trim(),
    city: component(place, 'locality') || component(place, 'sublocality') || component(place, 'postal_town'),
    state: component(place, 'administrative_area_level_1', true),
    zip: String(zip || component(place, 'postal_code')).match(/\d{5}/)?.[0] || '',
    lat: Number.isFinite(lat) ? lat : null,
    lng: Number.isFinite(lng) ? lng : null,
  };
}

export function normalizedAddressKey(address) {
  const a = address || {};
  return [a.line1, a.city, a.state, a.zip]
    .map(part => String(part || '').toLowerCase().replace(/[.,#]/g, ' ').replace(/\s+/g, ' ').trim())
    .join('|');
}

/** Accepts only a complete in-test answer; anything else is "not in test". */
export function sanitizeSplitResponse(raw) {
  if (!raw || typeof raw !== 'object' || raw.in_test !== true) return { in_test: false };
  if (!ARMS.includes(raw.arm) || typeof raw.assignment_id !== 'string' || !raw.assignment_id) {
    return { in_test: false };
  }
  const str = v => (typeof v === 'string' && v.trim() ? v.trim() : null);
  const phoneE164 = /^\+\d{10,15}$/.test(raw.phone_e164 || '') ? raw.phone_e164 : null;
  const phoneDisplay = str(raw.phone_display);
  return {
    in_test: true,
    arm: raw.arm,
    experiment_id: str(raw.experiment_id),
    brand: str(raw.brand),
    brand_display_name: str(raw.brand_display_name),
    assignment_id: raw.assignment_id,
    phone_display: phoneDisplay && phoneE164 ? phoneDisplay : null,
    phone_e164: phoneDisplay && phoneE164 ? phoneE164 : null,
    plans: Array.isArray(raw.plans)
      ? raw.plans.filter(p => p && str(p.key) && str(p.label)).map(p => ({ key: str(p.key), label: str(p.label) }))
      : [],
  };
}

export function getCurrentSplitAssignment() {
  const current = readJson(CURRENT_KEY);
  return current && current.in_test === true ? current : null;
}

function setCurrent(assignment) {
  writeJson(CURRENT_KEY, assignment && assignment.in_test ? assignment : null);
  try { window.dispatchEvent(new Event(SPLIT_EVENT)); } catch { /* no window in tests */ }
}

/** Remember a direct-arm answer the page will restore, including a zip fallback. */
export function publishSplitAssignment(assignment) {
  setCurrent(assignment);
}

/**
 * A direct-arm visitor who answers "Business" leaves the short form for the
 * normal commercial funnel. The processor routes commercial submissions to
 * the normal path even for a direct assignment, so the id still rides along.
 */
export function markDirectCommercial() {
  const current = getCurrentSplitAssignment();
  if (current?.arm === 'direct') setCurrent({ ...current, commercial: true });
}

/** The id to stamp on normal-funnel payloads, or undefined when not in test. */
export function splitAssignmentIdForPayload() {
  const current = getCurrentSplitAssignment();
  if (current?.arm === 'cabana' || (current?.arm === 'direct' && current.commercial === true)) {
    return current.assignment_id;
  }
  return undefined;
}

export function isDirectArmActive() {
  const current = getCurrentSplitAssignment();
  return current?.arm === 'direct' && current.commercial !== true;
}

export function subscribeSplitAssignment(listener) {
  const handler = () => listener(getCurrentSplitAssignment());
  window.addEventListener(SPLIT_EVENT, handler);
  return () => window.removeEventListener(SPLIT_EVENT, handler);
}

// One call to the route, bounded by timeoutMs. Outcomes:
//   { confirmed: answer } - a 200 with a readable body; safe to cache.
//   { uncertain: true }   - client timeout, network error, 5xx (the route's
//                           503) or unreadable body; worth one retry.
//   { failed: true }      - any other status (a 4xx); fail open, no retry.
async function attemptSplitAssign(fetchImpl, body, timeoutMs) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timer;
  const timeout = new Promise(resolve => {
    timer = setTimeout(() => { controller?.abort(); resolve({ uncertain: true }); }, timeoutMs);
  });
  const call = (async () => {
    try {
      const response = await fetchImpl('/api/split-assign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        signal: controller?.signal,
      });
      if (response.status >= 500) return { uncertain: true };
      if (!response.ok) return { failed: true };
      return { confirmed: sanitizeSplitResponse(await response.json()) };
    } catch { return { uncertain: true }; }
  })();
  try { return await Promise.race([call, timeout]); } finally { clearTimeout(timer); }
}

/**
 * Resolves to the sanitized assignment, or null when not in test, disabled,
 * uncertain after one retry, or failed. Never throws. An uncertain first
 * attempt is retried once with the same budget (the processor returns the
 * existing arm for the same stable id and address), so the wait is bounded
 * by twice timeoutMs. Only confirmed answers are cached.
 */
export async function requestSplitAssignment({
  address, stableId, quoteSession = null, host = window.location.hostname,
  fetchImpl = (...args) => fetch(...args), timeoutMs = SPLIT_ASSIGN_TIMEOUT_MS,
  enabled = isSplitAssignEnabled(),
}) {
  if (!enabled || !stableId || !address?.zip) {
    setCurrent(null);
    return null;
  }
  const qa = getSplitQa();
  const cacheKey = `${CACHE_PREFIX}${stableId}|${normalizedAddressKey(address)}|${qa?.arm || ''}`;
  const cached = readJson(cacheKey);
  if (cached && typeof cached.in_test === 'boolean') {
    setCurrent(cached);
    return cached.in_test ? cached : null;
  }
  const body = JSON.stringify({
    stable_id: stableId, host, quote_session: quoteSession, address,
    qa_token: qa?.token || null, qa_arm: qa?.arm || null,
  });
  let outcome = await attemptSplitAssign(fetchImpl, body, timeoutMs);
  if (outcome.uncertain) outcome = await attemptSplitAssign(fetchImpl, body, timeoutMs);
  const result = outcome.confirmed || null;
  if (result) writeJson(cacheKey, result);
  setCurrent(result);
  return result && result.in_test ? result : null;
}
